import { DomainUserUtil } from './../utils/domain-user-util';
import { ValidatorFn, AbstractControl, Validators, UntypedFormGroup } from '@angular/forms';
import { ipV4Pattern, ipV6Pattern } from '../regex-patterns';
import { DateTime } from 'luxon';
import { ExportInterfaceConfigurationDecimalSeparator } from '../../models/models.swagger.generated';

export class CustomValidatorsFactory {
  public static createPhoneValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      return Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)(control)
        ? {
          phone: {
            error: true
          }
        }
        : null
    }
  }

  public static createZipValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      return Validators.pattern(/^[1-9]\d{3}$/)(control)
        ? {
          zip: {
            error: true
          }
        }
        : null
    }
  }

  public static createNoDomainUserValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        return DomainUserUtil.isDomainUser(control.value) ? { noDomainUserAllowed: true } : null;
      }
      return null;
    };
  }

  public static createNoWhiteSpacesAllowed(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return Validators.pattern(/^\S*$/)(control) ? { noWhiteSpacesAllowed: true } : null;
    };
  }

  public static passwordValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        // password should contain at least 6 digits, 1 upppercase, 1 lowercase, 1 number
        const pwdRule = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/g);
        const validPassword = pwdRule.test(control.value);
        return !validPassword ? { invalidPassword: true } : null;
      }
      return null;
    };
  }

  public static passwordMatchValidation(passwordControlName = 'password', passwordConfirmControlName = 'passwordConfirm'): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const password = group.get(passwordControlName).value;
      const passwordConfirm = group.get(passwordConfirmControlName).value;
      if (!password || !passwordConfirm) {
        return null;
      }
      return password === passwordConfirm ? null : { passwordConfirmationMismatch: { valid: false } };
    };
  }

  public static emailValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const emailRule = new RegExp(/^[^@\s]+@[^@\s]+\.[^@\s]+$/g);
        const validEmail = emailRule.test(control.value);
        return !validEmail ? { email: true } : null;
      }
      return null;
    };
  }

  public static forbiddenNameValidator(nameRe: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = nameRe.test(control.value);
      return forbidden ? { forbiddenName: { value: control.value } } : null;
    };
  }

  public static datePickerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const date = DateTime.fromJSDate(control.value);
        let valid = date.isValid;
        if (valid) {
          if (date < DateTime.now().startOf('day') || date > DateTime.now().plus({ years: 1 })) {
            valid = false;
          }
        }
        return !valid ? { invalidDate: true } : null;
      } else {
        return null;
      }
    };
  }

  public static atLeastOneFromFormGroupRequired(): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const isTrue = (element) => !!group.get(element).value;
      const valid = Object.keys(group.controls).some(isTrue);
      return valid ? null : { error: true };
    };
  }

  public static atLeastOneRequired(controls: string[] = null): ValidatorFn {
    return (group: UntypedFormGroup) => {
      if (!controls) {
        controls = Object.keys(group.controls)
      }

      const hasAtLeastOne = group && group.controls && controls
        .some(k => !Validators.required(group.controls[k]));

      return hasAtLeastOne ? null : {
        atLeastOne: true,
      };
    };
  };

  public static validateIpAddress(allowIPv6: boolean = true): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        let regexp;
        if (allowIPv6) {
          regexp = new RegExp(`(${ipV6Pattern})|(${ipV4Pattern})`, 'i');
          return regexp.test(control.value) ? null : { ipV6Address: true };
        } else {
          regexp = new RegExp(`${ipV4Pattern}`, 'i');
          return regexp.test(control.value) ? null : { ipAddress: true };
        }
      }
      return null;
    };
  }

  public static compareFieldSeparaterAndDecimalSeparater(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const fieldSeparator = control.get('seperatorCharacter').value;
        const decimalSeparator = control.get('decimalSeperator').value as ExportInterfaceConfigurationDecimalSeparator;
        let decimalSeparatorString = '';
        // map decimal separator to string
        if (decimalSeparator === ExportInterfaceConfigurationDecimalSeparator.Comma) {
          decimalSeparatorString = ',';
        }
        if (decimalSeparator === ExportInterfaceConfigurationDecimalSeparator.Point) {
          decimalSeparatorString = '.';
        }
        return fieldSeparator === decimalSeparatorString ? { fieldAndDecimalSeparator: true } : null;
      }
      return null;
    };
  }

  public static compareFieldSeparaterAndDecimalSeparaterOnControl(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value) {
        const fieldSeparator = control.parent?.get('seperatorCharacter').value;
        const decimalSeparator = control.parent?.get('decimalSeperator').value as ExportInterfaceConfigurationDecimalSeparator;
        let decimalSeparatorString = '';
        // map decimal separator to string
        if (decimalSeparator === ExportInterfaceConfigurationDecimalSeparator.Comma) {
          decimalSeparatorString = ',';
        }
        if (decimalSeparator === ExportInterfaceConfigurationDecimalSeparator.Point) {
          decimalSeparatorString = '.';
        }
        return fieldSeparator === decimalSeparatorString ? { separatorNotAllowed: true }
        : null
      }
      return null;
    };
  }
}
